import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Heading,
  Icon,
  Text,
  VStack
} from "@chakra-ui/react";
import { isLoggedIn } from "@lubbezposrednio-frontend/core";
import { PageHeader } from "@lubbezposrednio-frontend/commons";
import { AdaptiveShell } from "./OrganizerDashboardView/components/AdaptiveShell";

/** Nagłówkowy band strony pomocowej — fioletowy gradient. */
export const InfoHero: React.FC<{ eyebrow: string; title: string; subtitle?: string }> = ({ eyebrow, title, subtitle }) => (
  <Box as="section" w="100%" bgGradient="linear(160deg, #430098 0%, #6a1fc2 60%, #8b2fd6 100%)" color="white" position="relative" overflow="hidden">
    <Box position="absolute" top="-110px" right="-60px" w="400px" h="400px" borderRadius="full" bg="rgba(229,31,68,0.18)" filter="blur(75px)" />
    <VStack maxW="860px" mx="auto" px={{ base: "20px", md: "40px" }} py={{ base: "56px", md: "84px" }} spacing="16px" align="center" textAlign="center" position="relative" zIndex={1}>
      <Text fontSize="13px" fontWeight="800" letterSpacing="0.1em" textTransform="uppercase" color="#ff9bb0">
        {eyebrow}
      </Text>
      <Heading fontSize={{ base: "32px", md: "46px" }} fontWeight="900" lineHeight="1.1" letterSpacing="-0.02em">
        {title}
      </Heading>
      {subtitle && (
        <Text fontSize={{ base: "16px", md: "19px" }} color="rgba(255,255,255,0.88)" lineHeight="1.6" maxW="620px">
          {subtitle}
        </Text>
      )}
    </VStack>
  </Box>
);

/** Kontener treści. Public → pełnoszerokościowy band z tłem; panel → treść na tle panelu (bez seamu). */
export const InfoBody: React.FC<{ children: React.ReactNode; bg?: string; narrow?: boolean }> = ({ children, bg, narrow }) =>
  isLoggedIn() ? (
    <Box maxW={narrow ? "760px" : "920px"} mx="auto" px={{ base: "20px", md: "40px" }} py={{ base: "32px", md: "44px" }}>
      {children}
    </Box>
  ) : (
    <Box as="section" w="100%" bg={bg ?? "white"}>
      <Box maxW={narrow ? "760px" : "920px"} mx="auto" px={{ base: "20px", md: "40px" }} py={{ base: "48px", md: "72px" }}>
        {children}
      </Box>
    </Box>
  );

export const InfoSectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Heading fontSize={{ base: "24px", md: "30px" }} fontWeight="800" color="primary" mb={{ base: "24px", md: "32px" }}>
    {children}
  </Heading>
);

/** Pionowa, numerowana lista kroków z łączącą linią. */
export const NumberedSteps: React.FC<{ steps: { icon?: React.ElementType; title: string; desc: string }[] }> = ({ steps }) => (
  <VStack align="stretch" spacing="0">
    {steps.map((s, i) => (
      <Flex key={s.title} gap={{ base: "16px", md: "22px" }} align="stretch">
        <VStack spacing="0" align="center" flexShrink={0}>
          <Flex w="46px" h="46px" borderRadius="full" bg="accent" color="white" fontWeight="800" fontSize="17px" align="center" justify="center" boxShadow="0 8px 18px rgba(229,31,68,0.25)">
            {i + 1}
          </Flex>
          {i < steps.length - 1 && <Box w="2px" flex="1" bg="rgba(67,0,152,0.12)" my="8px" minH="24px" />}
        </VStack>
        <Box pb={i === steps.length - 1 ? 0 : "30px"} pt="6px">
          <Flex align="center" gap="9px" mb="5px">
            {s.icon && <Icon as={s.icon} color="primary" boxSize="18px" />}
            <Heading fontSize={{ base: "18px", md: "20px" }} fontWeight="800" color="primary">
              {s.title}
            </Heading>
          </Flex>
          <Text fontSize={{ base: "15px", md: "16px" }} color="gray.600" lineHeight="1.6">
            {s.desc}
          </Text>
        </Box>
      </Flex>
    ))}
  </VStack>
);

/** Akordeon FAQ. */
export const FaqList: React.FC<{ items: { q: string; a: string }[] }> = ({ items }) => (
  <Accordion allowMultiple>
    {items.map(f => (
      <AccordionItem key={f.q} border="none" mb="12px" bg="white" borderRadius="16px" overflow="hidden" boxShadow="0 4px 14px rgba(67,0,152,0.05)">
        <AccordionButton py="18px" px="22px" _hover={{ bg: "rgba(67,0,152,0.02)" }}>
          <Box flex="1" textAlign="left" fontWeight="700" color="primary" fontSize={{ base: "15px", md: "16px" }}>
            {f.q}
          </Box>
          <AccordionIcon color="accent" />
        </AccordionButton>
        <AccordionPanel px="22px" pb="20px" color="gray.600" fontSize="15px" lineHeight="1.65">
          {f.a}
        </AccordionPanel>
      </AccordionItem>
    ))}
  </Accordion>
);

/**
 * Pełny shell strony pomocowej.
 * Public → kolorowe hero (gradient). Zalogowany panel → spójny PageHeader (bez kolorowego hero).
 */
export const InfoPage: React.FC<{ eyebrow: string; title: string; subtitle?: string; children: React.ReactNode }> = ({ eyebrow, title, subtitle, children }) =>
  isLoggedIn() ? (
    <AdaptiveShell>
      <PageHeader title={title} subtitle={subtitle} />
      {children}
    </AdaptiveShell>
  ) : (
    <AdaptiveShell>
      <InfoHero eyebrow={eyebrow} title={title} subtitle={subtitle} />
      {children}
    </AdaptiveShell>
  );
